/**
 * Declarative spec for the bot config knobs, grouped into sections by MEANING
 * (not a flat grid). Drives both the defaults editors (org + personal Settings)
 * and the spawn form's advanced popover, so there's one source of truth for
 * what each kind exposes, how it's labelled, and which scope may edit it.
 *
 * Field keys mirror the g10edge strategy config (VanillaConfig /
 * QueueRecyclerConfig). Every field is optional everywhere: an unset value
 * inherits the next layer (personal → org → edge built-in). `orgOnly` sections
 * are strategy decisions a trader shouldn't tune — they appear in the org
 * defaults editor only, never in a trader's personal panel or spawn popover.
 */

export type BotUnit = '¢' | 'ct' | '¢/s' | 'ct/s' | '%';

export interface BotField {
  key: string;
  label: string;
  help?: string;
  unit?: BotUnit;
  /** Edge built-in default, shown as the input placeholder ("inherits N"). */
  builtin?: number;
  /**
   * Sets `min={0}` on the spawn form's inputs. NOT what rejects negatives in
   * the defaults editor — that is unconditional there (`parseKnobInput`),
   * because the stored contract refuses a negative on EVERY knob regardless of
   * this flag. A field marked `nonNegative: false` would still be rejected by
   * the relay, so the flag is a presentation hint, not a per-field contract.
   */
  nonNegative?: boolean;
}

export interface BotSection {
  title: string;
  fields: BotField[];
  /** Strategy-level knobs — org defaults editor only, hidden from traders. */
  orgOnly?: boolean;
}

export interface BotKindSpec {
  /** Edge kind string sent on spawn. */
  kind: string;
  /** Trader-facing family label. */
  label: string;
  /** Fields a trader routinely sets at spawn time (the minimal form shows these
   *  + contracts); everything else lives behind "advanced". */
  primary: string[];
  sections: BotSection[];
}

export const BOT_KIND_SPECS: BotKindSpec[] = [
  {
    kind: 'vanillamm',
    label: 'Vanilla MM',
    // Fill avoidance LEADS and is part of `primary`: a vanilla bot's entire job is
    // to hold queue presence WITHOUT getting filled, so its recycle guards are the
    // most important knobs — surfaced up front (not buried behind "Advanced", not
    // hidden as org-only). Size stays primary[0] (the contracts input the form
    // renders specially); the two core guards follow it prominently.
    primary: ['sizeContracts', 'frontDistanceThreshold', 'proximityRatioPct'],
    sections: [
      {
        title: 'Fill avoidance',
        fields: [
          { key: 'frontDistanceThreshold', label: 'Front distance', unit: 'ct', help: 'Recycle (cancel→replace at the back) when within this many contracts of the front — the core "don\'t get filled" guard', builtin: 15000, nonNegative: true },
          { key: 'proximityRatioPct', label: 'Proximity', unit: '%', help: 'Recycle when the queue ahead falls to this % of where it was at placement (you\'ve advanced toward the front)', builtin: 75, nonNegative: true },
          { key: 'velocityPullCentsPerSec', label: 'Line-velocity pull', unit: '¢/s', help: 'Pull the leg when the line moves this fast against it · 0 = off', builtin: 0, nonNegative: true },
          { key: 'depthVelocityPullPerSec', label: 'Depth-velocity pull', unit: 'ct/s', help: 'Pull when the queue ahead clears this fast · 0 = off', builtin: 0, nonNegative: true },
          { key: 'polyMoveCents', label: 'Poly-move pull', unit: '¢', help: 'Pull when Polymarket leads against the leg · 0 = off', builtin: 0, nonNegative: true },
          { key: 'closeFrontThreshold', label: 'Close-front', unit: 'ct', help: 'Gate the hard pulls within this many contracts ahead', builtin: 15000, nonNegative: true },
        ],
      },
      {
        title: 'Quote shape',
        fields: [
          { key: 'sizeContracts', label: 'Size', unit: 'ct', help: 'Contracts per side', builtin: 1, nonNegative: true },
          { key: 'belowCents', label: 'Bid offset', unit: '¢', help: 'Below the mid', builtin: 1, nonNegative: true },
          { key: 'aboveCents', label: 'Ask offset', unit: '¢', help: 'Above the mid', builtin: 2, nonNegative: true },
          { key: 'recenterCents', label: 'Recenter', unit: '¢', help: 'Reprice a leg once it drifts beyond this', builtin: 2, nonNegative: true },
        ],
      },
      {
        title: 'Caps',
        fields: [
          { key: 'perSideCapContracts', label: 'Per-side cap', unit: 'ct', help: 'Max resting per side · 0 = uncapped', builtin: 0, nonNegative: true },
        ],
      },
    ],
  },
  {
    kind: 'queuerecycler',
    label: 'Recycler',
    primary: ['targetContracts', 'frontDistanceThreshold', 'proximityRatioPct', 'pullToxicityRatioPct'],
    sections: [
      {
        title: 'Fill avoidance',
        fields: [
          { key: 'frontDistanceThreshold', label: 'Front distance', unit: 'ct', help: 'Recycle a chunk when its queue position is at/under this many ahead — the core "don\'t get filled" guard', builtin: 15000, nonNegative: true },
          { key: 'proximityRatioPct', label: 'Proximity', unit: '%', help: 'Recycle (rotate to the back) when the queue ahead falls to this % of where it was at placement — the "1M ahead, move to back at 500k" knob. Higher = recycle sooner', builtin: 75, nonNegative: true },
          { key: 'pullToxicityRatioPct', label: 'Pull toxicity', unit: '%', help: 'Retreat a side when the queue ahead looks pull-heavy · 0 = off', builtin: 0, nonNegative: true },
          { key: 'velocityPullCentsPerSec', label: 'Velocity pull', unit: '¢/s', help: 'Pull when the line moves this fast against it · 0 = off', builtin: 0, nonNegative: true },
          { key: 'recenterTolCents', label: 'Recenter tol', unit: '¢', help: 'Reprice tolerance', builtin: 1, nonNegative: true },
          { key: 'maxRecyclesPerTick', label: 'Max recycles/tick', unit: 'ct', help: 'Cap recycles per tick', builtin: 1, nonNegative: true },
          { key: 'polyDangerCents', label: 'Poly danger', unit: '¢', help: 'Pull when Polymarket leads against the leg · 0 = off', builtin: 0, nonNegative: true },
        ],
      },
      {
        title: 'Quote shape',
        fields: [
          { key: 'targetContracts', label: 'Target', unit: 'ct', help: 'Resting target per side', builtin: 1, nonNegative: true },
          { key: 'chunkContracts', label: 'Chunk', unit: 'ct', help: 'Contracts per recycle chunk', builtin: 1, nonNegative: true },
          { key: 'halfSpreadCents', label: 'Half-spread', unit: '¢', help: 'Quote this far off the mid', builtin: 3, nonNegative: true },
        ],
      },
      {
        title: 'Band',
        fields: [
          { key: 'bandMinCents', label: 'Band min', unit: '¢', help: 'Lowest price the bot will quote', builtin: 5, nonNegative: true },
          { key: 'bandMaxCents', label: 'Band max', unit: '¢', help: 'Highest price the bot will quote', builtin: 95, nonNegative: true },
        ],
      },
      {
        // Strategy-level classification threshold — org defaults editor only,
        // hidden from traders. Splits the ahead-of-us flow into institutional
        // ("banks") vs recreational ("marks") by trade/order size, feeding the
        // FleetBoard inst-vs-rec badge + rest-quality composite.
        title: 'Classification',
        orgOnly: true,
        fields: [
          { key: 'institutionalMinContracts', label: 'Institutional min', unit: 'ct', help: 'A single trade/order at or above this size counts as institutional ("banks"); smaller trades are recreational ("marks"). Big contracts filling ahead = real demand to rest in front of', builtin: 1000, nonNegative: true },
        ],
      },
    ],
  },
];

export function specForKind(kind: string): BotKindSpec | undefined {
  return BOT_KIND_SPECS.find((s) => s.kind === kind);
}

/**
 * What one knob input's raw text means. `clear` deletes the override so the
 * next layer is inherited; `value` is a storable magnitude; `invalid` is text
 * the stored contract would reject.
 */
export type KnobInput =
  | { kind: 'clear' }
  | { kind: 'value'; value: number }
  | { kind: 'invalid'; message: string };

/**
 * Read one bot-knob input the way the stored contract does, so the editor can
 * refuse a value before a save request carries it (#312).
 *
 * This mirrors the SEMANTIC rule enforced by `isKindConfig` in
 * `src/lib/bots/botDefaults.ts` — finite and non-negative — but deliberately
 * lives here rather than there. `botDefaults.ts` is byte-mirrored into
 * `relay/src/lib/bots/botDefaults.ts` and pinned by `mirror.test.ts`, so a
 * string-parsing helper only the browser needs would ship into relay code on
 * every edit. `configSpec` is the frontend-only knob catalog (#305 kept it that
 * way on purpose), which makes it the right home for a frontend-only reader.
 *
 * The relay stays the final authority for hand-crafted or stale clients; this
 * only buys the trader inline feedback instead of a rejected PATCH.
 *
 * No maximum is applied. The stored contract has none, and inventing one here
 * would refuse values the relay accepts.
 */
export function parseKnobInput(raw: string): KnobInput {
  // Blank keeps its existing meaning — inherit the next layer. Checked first
  // because `Number('')` and `Number('  ')` are both 0, which would otherwise
  // turn "cleared" into a stored zero (and 0 means "off", not "unset").
  if (raw.trim() === '') return { kind: 'clear' };

  const n = Number(raw);
  // Rejects NaN from unparseable text AND ±Infinity from an overflowing literal
  // such as `1e999`. The overflow branch is genuinely reachable: the editor
  // reads raw text (see BotDefaultsEditor's input), so the string arrives here
  // intact rather than being blanked by a number input's sanitization.
  if (!Number.isFinite(n)) return { kind: 'invalid', message: 'Enter a number' };
  if (n < 0) return { kind: 'invalid', message: 'Must be 0 or more' };

  return { kind: 'value', value: n };
}
